import { useEffect } from 'react'
import type { AnalyticsServiceSettings } from 'models/AnalyticsServiceSettings'
import { logger } from 'utils/logging'

/**
 * This hook supports an NPOs custom Optimizely script to be loaded on the page
 */

type OptimizelyParameters = {
  project_id: string
}

function isOptimizelyParameters(params: unknown): params is OptimizelyParameters {
  return typeof params === 'object' && params !== null && 'project_id' in params
}

/**
 * If organization Optimizely is allowed (MSF/doctorswithoutborders), extract the
 * project ID from analyticsServiceSettings. Otherwise, return undefined.
 */
export function getOrgOptimizelyProjectId(
  allowOptimizely: boolean,
  analyticsServiceSettings: AnalyticsServiceSettings[],
) {
  if (!allowOptimizely) {
    return null
  }

  const optimizely = analyticsServiceSettings.find(
    (setting) => setting.service_name === 'optimizely',
  )

  if (optimizely && isOptimizelyParameters(optimizely.parameters)) {
    return optimizely.parameters.project_id
  }

  return null
}

/**
 * Custom hook to handle Optimizely page (de)activation lifecycle.
 * https://docs.developers.optimizely.com/web-experimentation/docs/react-server-side-rendering-and-hydration#best-practices
 *
 * Important! Optimizely is fundamentally a tool for A/B testing and feature flagging, not a
 * traditional analytics platform. While analyticsSettings is not the ideal model for Optimizely,
 * it's the most pragmatically aligned integration model available (per ApiV2).
 *
 * ! Note, Optimizely is only meant for MSF (doctorswithoutborders) campaigns.
 * ! We should not be opening it up for other orgs.
 */
export function useOptimizelyPageLifecycle(organizationId: string, orgOptimizelyProjectId: string) {
  useEffect(() => {
    if (!orgOptimizelyProjectId || orgOptimizelyProjectId === '') {
      return
    }

    if (orgOptimizelyProjectId && typeof window !== 'undefined' && window.optimizely) {
      logger('info', 'Org optimizely forcing necessary reactivation post-hydration', {
        context: { organizationId, orgOptimizelyProjectId },
      })

      const optimizely = window.optimizely

      const optimizelyPages = optimizely.get('data').pages

      Object.keys(optimizelyPages).forEach((pageId) => {
        // Deactivate page
        optimizely.push({
          type: 'page',
          pageName: optimizelyPages[pageId].apiName,
          isActive: false,
        })

        // Reactivate page
        optimizely.push({
          type: 'page',
          pageName: optimizelyPages[pageId].apiName,
        })
      })
    }
  }, [organizationId, orgOptimizelyProjectId])
}
